import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { notifyDataChanged } from '../utils/dataEvents';
import { Input, Textarea, CustomSelect, Label, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { createMilestone, updateMilestone } from '../services/api';
import { useMilestoneStore } from '../stores/pmMilestoneStore';
import { saveOptimistic } from '../stores/createProjectItemStore';

import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { PMMilestone, PMPackage } from '../types/pm';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface MilestoneDialogProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  projectId: string;
  milestone?: PMMilestone | null;
  nextOrderIndex: number; // 0 起算（與資料庫一致），表單中以 1 起算顯示
  totalBudget?: number | null;
  packages?: PMPackage[];
}

interface FormData {
  name: string;
  description?: string;
  orderIndex: number;
  dueDate?: string;
  paymentPercentage?: number;
  paymentAmount?: number;
  packageId?: string;
}

const MilestoneDialog: React.FC<MilestoneDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  milestone,
  nextOrderIndex,
  totalBudget,
  packages = [],
}) => {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();
  const { t } = useTranslation(['translation', 'pages']);
  const orderIndexInput = useNumberInput('integer');
  const paymentPercentageInput = useNumberInput('integer');
  const paymentAmountInput = useNumberInput('integer');
  const editingField = useRef<'percentage' | 'amount' | null>(null);

  // 預先取出 register 結果，才能同時呼叫 rhf onChange 和 useNumberInput handleChange
  const percentageReg = register('paymentPercentage', {
    ...paymentPercentageInput.validationRules,
    validate: (value) => {
      if (!value) return true;
      const num = parseInt(value as any);
      if (num < 0 || num > 100) {
        return t('pmMilestones.validation.paymentPercentageRange', { ns: 'pages' }) || 'Percentage must be between 0 and 100';
      }
      return true;
    },
  });
  const amountReg = register('paymentAmount', paymentAmountInput.validationRules);

  const isEditMode = !!milestone;

  const actualHandleClose = () => {
    reset();
    onClose(false);
  };

  const {
    handleClose: handleCloseWithWarning,
    showConfirm,
    handleConfirmClose,
    handleCancelClose,
  } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  // 雙向聯動：比例 ↔ 金額（有選分包時以該包金額為基準，否則以專案總預算為基準）
  const watchedPercentage = watch('paymentPercentage');
  const watchedAmount = watch('paymentAmount');
  const watchedPackageId = watch('packageId', '');
  const selectedPackage = packages.find(p => p.id === watchedPackageId);
  const baseAmount = selectedPackage ? selectedPackage.amount : totalBudget;

  useEffect(() => {
    if (!baseAmount || editingField.current !== 'percentage') return;
    const pct = Number(watchedPercentage);
    if (!isNaN(pct) && pct > 0) {
      setValue('paymentAmount', Math.round(baseAmount * pct / 100) as any, { shouldDirty: false });
    }
  }, [watchedPercentage]);

  useEffect(() => {
    if (!baseAmount || editingField.current !== 'amount') return;
    const amt = Number(watchedAmount);
    if (!isNaN(amt) && amt > 0) {
      setValue('paymentPercentage', Math.round(amt / baseAmount * 100) as any, { shouldDirty: false });
    }
  }, [watchedAmount]);

  // 切換分包時，依原比例重算金額
  const handlePackageChange = (value: string) => {
    setValue('packageId', value, { shouldDirty: true });
    const base = packages.find(p => p.id === value)?.amount ?? totalBudget;
    const pct = Number(watchedPercentage);
    if (base && !isNaN(pct) && pct > 0) {
      setValue('paymentAmount', Math.round(base * pct / 100) as any, { shouldDirty: true });
    }
  };

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      const today = getTodayDateInput();
      if (milestone) {
        // 編輯模式：使用 reset 設置初始值，避免標記為 dirty
        reset({
          name: milestone.name,
          description: milestone.description || '',
          orderIndex: milestone.orderIndex + 1, // 表單以 1 起算顯示，與列表一致
          dueDate: milestone.dueDate ? toDateInput(milestone.dueDate) : '',
          paymentPercentage: milestone.paymentPercentage || undefined,
          paymentAmount: milestone.paymentAmount || undefined,
          packageId: milestone.packageId || '',
        });
      } else {
        // 新增模式：使用 reset 設置預設值
        reset({
          name: '',
          description: '',
          orderIndex: nextOrderIndex + 1,
          dueDate: today,
          paymentPercentage: undefined,
          paymentAmount: undefined,
          // 新增時預設歸入最後一個未完成的分包（通常就是目前正在做的那包）
          packageId: [...packages].reverse().find(p => p.status !== 'COMPLETED' && p.status !== 'CANCELLED')?.id
            || packages[packages.length - 1]?.id
            || '',
        });
      }
    }
    // 只在開啟、換編輯對象時帶入；不依賴 packages（分包重新載入時會清掉使用者輸入到一半的內容）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, milestone, nextOrderIndex, reset]);

  const onSubmit = async (data: FormData) => {
    const payload: any = {
      name: data.name.trim(),
      description: data.description?.trim() || null,
      orderIndex: Math.max(0, Number(data.orderIndex) - 1), // 轉回 0 起算儲存
      dueDate: data.dueDate ? toISOString(data.dueDate) : null,
      paymentPercentage: data.paymentPercentage ? Number(data.paymentPercentage) : null,
      paymentAmount: data.paymentAmount ? Number(data.paymentAmount) : null,
      packageId: data.packageId || null,
    };

    const now = new Date().toISOString();
    const draft: PMMilestone = isEditMode
      ? { ...milestone, ...payload }
      : { id: `temp-${Date.now()}`, projectId, status: 'PENDING', createdAt: now, updatedAt: now, ...payload };
    onClose(false);

    try {
      await saveOptimistic(useMilestoneStore, {
        original: milestone,
        draft,
        request: () => (isEditMode ? updateMilestone(milestone.id, payload) : createMilestone(projectId, payload)),
      });
      // 里程碑換分包時，其收款會跟著移動：專案詳情整包重抓（含分包已收款）
      if (isEditMode && (milestone.packageId || null) !== payload.packageId) notifyDataChanged('packages');
      toast.success(t(isEditMode ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t(isEditMode ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <DialogShell onClose={handleCloseWithWarning} className="max-w-2xl">
        <DialogHeader title={isEditMode ? t('pmMilestones.editTitle', { ns: 'pages' }) || 'Edit Milestone' : t('pmMilestones.addTitle', { ns: 'pages' }) || 'Add Milestone'} onClose={handleCloseWithWarning} closeLabel={t('common.actions.close')} />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-6">
            {/* Name */}
            <FormFieldWithError
              label={t('pmMilestones.fields.name', { ns: 'pages' }) || 'Name'}
              required={true}
              error={errors.name?.message}
              htmlFor="name"
            >
              <Input
                id="name"
                {...register('name', {
                  required: t('pmMilestones.validation.nameRequired', { ns: 'pages' }) || 'Name is required',
                  maxLength: {
                    value: 200,
                    message: t('pmMilestones.validation.nameMaxLength', { ns: 'pages' }) || 'Name must not exceed 200 characters',
                  },
                })}
                variant={errors.name ? 'error' : 'default'}
                placeholder={t('pmMilestones.placeholders.name', { ns: 'pages' }) || 'Enter milestone name'}
              />
            </FormFieldWithError>

            {/* Description */}
            <FormFieldWithError
              label={t('pmMilestones.fields.description', { ns: 'pages' }) || 'Description'}
              error={errors.description?.message}
              htmlFor="description"
            >
              <Textarea
                id="description"
                {...register('description', {
                  maxLength: {
                    value: 2000,
                    message: t('pmMilestones.validation.descriptionMaxLength', { ns: 'pages' }) || 'Description must not exceed 2000 characters',
                  },
                })}
                variant={errors.description ? 'error' : 'default'}
                placeholder={t('pmMilestones.placeholders.description', { ns: 'pages' }) || 'Enter milestone description'}
                rows={3}
              />
            </FormFieldWithError>

            {/* Order Index & Due Date Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('pmMilestones.fields.orderIndex', { ns: 'pages' }) || 'Order'}
                required={true}
                error={errors.orderIndex?.message}
                htmlFor="orderIndex"
              >
                <Input
                  id="orderIndex"
                  type="text"
                  {...orderIndexInput.bind(register('orderIndex', orderIndexInput.validationRules))}
                  variant={errors.orderIndex ? 'error' : 'default'}
                  placeholder="0"
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmMilestones.fields.dueDate', { ns: 'pages'}) || 'Due Date'}
                required={true}
                error={errors.dueDate?.message}
                htmlFor="dueDate"
              >
                <Input
                  id="dueDate"
                  type="date"
                  {...register('dueDate', {
                    required: t('pmMilestones.validation.dueDateRequired', { ns: 'pages' }) || 'Due date is required',
                  })}
                  variant={errors.dueDate ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>

            {/* Package */}
            {packages.length > 0 && (
              <div>
                <Label htmlFor="packageId">{t('pmMilestones.fields.package', { ns: 'pages' }) || 'Package'}</Label>
                <CustomSelect
                  id="packageId"
                  value={watchedPackageId || ''}
                  onChange={handlePackageChange}
                  options={[
                    { value: '', label: t('pmMilestones.noPackage', { ns: 'pages' }) || 'No package' },
                    ...packages.map(p => ({ value: p.id, label: `${p.name}（$${p.amount.toLocaleString()}）` })),
                  ]}
                />
              </div>
            )}

            {/* Payment Amount & Payment Percentage Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('pmMilestones.fields.paymentAmount', { ns: 'pages' }) || '收款金額'}
                error={errors.paymentAmount?.message}
                htmlFor="paymentAmount"
              >
                <Input
                  id="paymentAmount"
                  type="text"
                  {...amountReg}
                  variant={errors.paymentAmount ? 'error' : 'default'}
                  placeholder="0"
                  onChange={(e) => { editingField.current = 'amount'; paymentAmountInput.handleChange(e); amountReg.onChange(e); }}
                  onBlur={(e) => { editingField.current = null; amountReg.onBlur(e); }}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmMilestones.fields.paymentPercentage', { ns: 'pages' }) || '收款比例（%）'}
                error={errors.paymentPercentage?.message}
                htmlFor="paymentPercentage"
              >
                <Input
                  id="paymentPercentage"
                  type="text"
                  {...percentageReg}
                  variant={errors.paymentPercentage ? 'error' : 'default'}
                  placeholder="0"
                  onChange={(e) => { editingField.current = 'percentage'; paymentPercentageInput.handleChange(e); percentageReg.onChange(e); }}
                  onBlur={(e) => { editingField.current = null; percentageReg.onBlur(e); }}
                />
              </FormFieldWithError>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleCloseWithWarning}
              className={dialogButtonClass.cancel}
            >
              {t('common.actions.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className={dialogButtonClass.primary}
            >
              {isEditMode
                ? t('common.actions.save') || 'Save'
                : t('common.actions.create') || 'Create'}
            </button>
          </DialogFooter>
        </form>
    </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('components.modal.unsavedChanges.title')}
        message={t('components.modal.unsavedChanges.message')}
        confirmText={t('components.modal.unsavedChanges.confirmButton')}
        cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default MilestoneDialog;
